import React, { useState, useEffect } from "react";
import { Button } from "src/components/ui/button";
import { Input } from "src/components/ui/input";
import { Label } from "src/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "src/components/ui/select";
import { Switch } from "src/components/ui/switch";
import { Separator } from "src/components/ui/separator";
import { Textarea } from "src/components/ui/textarea";
import { useContacts } from "src/features/contacts/hooks/useContacts";
import { ImageUploader } from "src/components/ImageUploader";
import { MicrositeBuilder } from "src/features/qr_campaigns/builder/MicrositeBuilder";
import { normalizeContent } from "src/features/qr_campaigns/builder/blockSchema";
import { AmenitiesEditor } from "./AmenitiesEditor";
import { VideoLinksEditor } from "./VideoLinksEditor";
import { TagPicker } from "src/features/tags/components/TagPicker";

// Turn any text into a clean URL slug (mirrors the backend normalize_slug):
// lowercase, non-alphanumerics → single hyphen, trimmed.
function slugify(text) {
  return String(text || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

export function PropertyListingForm({
  listing,
  onSubmit,
  onCancel,
  isPending,
  isRental = false,
}) {
  const [formData, setFormData] = useState({
    title: "",
    contact_id: "",
    listing_type: isRental ? "rental" : "sale",
    status: "draft",
    property_type: "",
    city: "",
    locality: "",
    bedrooms: "",
    bathrooms: "",
    area_sqft: "",
    floor_number: "",
    total_floors: "",
    furnishing_status: "",
    facing: "",
    launch_date: "",
    possession_date: "",
    description: "",
    asking_price: "",
    rent_amount: "",
    security_deposit: "",
    maintenance_charges: "",
    configuration: "",
    // Website / publishing
    offer_state: isRental ? "rental" : "new",
    price_band: "",
    show_in_portfolio: false,
    show_name: true,
    public_display_name: "",
    slug: "",
  });

  // Non-scalar fields kept in their own state (arrays/objects).
  const [amenities, setAmenities] = useState([]);
  const [videoLinks, setVideoLinks] = useState([]);
  // Applied tags as full objects (so newly-picked tags show before save); ids
  // are derived for the payload.
  const [appliedTags, setAppliedTags] = useState([]);
  const tagIds = appliedTags.map((t) => t.id);

  const { data: contactsData, fetch: fetchContacts } = useContacts();
  const contacts = contactsData.contacts || [];

  useEffect(() => {
    // Load contacts so the listing can be linked to its owner/seller.
    fetchContacts({ per_page: 100 });
  }, [fetchContacts]);

  useEffect(() => {
    if (listing) {
      setFormData({
        title: listing.title || "",
        contact_id: listing.contact?.id ? String(listing.contact.id) : "",
        listing_type: listing.listing_type || (isRental ? "rental" : "sale"),
        status: listing.status || "draft",
        property_type: listing.property_type || "",
        city: listing.city || "",
        locality: listing.locality || "",
        bedrooms: listing.bedrooms || "",
        bathrooms: listing.bathrooms || "",
        area_sqft: listing.area_sqft || "",
        floor_number: listing.floor_number || "",
        total_floors: listing.total_floors || "",
        furnishing_status: listing.furnishing_status || "",
        facing: listing.facing || "",
        launch_date: listing.launch_date || "",
        possession_date: listing.possession_date || "",
        description: listing.description || "",
        asking_price: listing.asking_price || "",
        rent_amount: listing.rent_amount || "",
        security_deposit: listing.security_deposit || "",
        maintenance_charges: listing.maintenance_charges || "",
        configuration: listing.configuration || "",
        offer_state: listing.offer_state || (isRental ? "rental" : "new"),
        price_band: listing.price_band || "",
        show_in_portfolio: Boolean(listing.show_in_portfolio),
        show_name: listing.display_options?.show_name !== false,
        public_display_name: listing.public_display_name || "",
        slug: listing.slug || "",
      });
      setAmenities(Array.isArray(listing.amenities) ? listing.amenities : []);
      setVideoLinks(Array.isArray(listing.video_links) ? listing.video_links : []);
      setAppliedTags(Array.isArray(listing.tags) ? listing.tags : []);
    }
  }, [listing, isRental]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const [contactError, setContactError] = useState("");

  // Photo gallery changes, merged into the submit payload under __images.
  const [photos, setPhotos] = useState({ newFiles: [], removedIds: [] });
  useEffect(() => {
    setPhotos({ newFiles: [], removedIds: [] });
  }, [listing]);

  // The public-page block content ({ version, blocks: [...] }). Editable via the
  // drag-drop builder below; image blocks reference the listing's saved photos.
  const [websiteContent, setWebsiteContent] = useState(
    normalizeContent(listing?.website_content),
  );
  useEffect(() => {
    setWebsiteContent(normalizeContent(listing?.website_content));
  }, [listing]);

  // Saved photos are the image source for the builder ({ id, url, filename }).
  const savedPhotos = listing?.photos || [];

  const handleSubmit = (e) => {
    e.preventDefault();
    setContactError("");
    // Contact is optional — broker-owned website listings need not name a seller.
    const { show_name, ...rest } = formData;
    onSubmit({
      ...rest,
      display_options: { show_name },
      amenities,
      video_links: videoLinks.filter((v) => v.trim()),
      tag_ids: tagIds,
      website_content: websiteContent,
      __images: photos,
    });
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="flex flex-col h-full overflow-hidden"
    >
      <div className="flex-1 overflow-y-auto p-6 space-y-6">
        <div className="space-y-4">
          <div>
            <Label htmlFor="title">Name / Title *</Label>
            <Input
              id="title"
              name="title"
              required
              placeholder="e.g. Sunrise Towers — 3BHK"
              value={formData.title}
              onChange={handleChange}
            />
          </div>

          {/* Show / hide the name on the public page */}
          <div className="flex items-center justify-between rounded-lg border border-slate-200 p-3">
            <div>
              <Label htmlFor="show_name" className="cursor-pointer">Show name on website</Label>
              <p className="mt-0.5 text-xs text-slate-500">
                Off = the name stays private. A public display name (or an auto label) is shown instead.
              </p>
            </div>
            <Switch
              id="show_name"
              checked={formData.show_name}
              onCheckedChange={(val) => setFormData((prev) => ({ ...prev, show_name: val }))}
            />
          </div>

          {!formData.show_name && (
            <div>
              <Label htmlFor="public_display_name">Public display name (optional)</Label>
              <Input
                id="public_display_name"
                name="public_display_name"
                placeholder="e.g. Premium 3BHK near IT Park (blank → auto label)"
                value={formData.public_display_name}
                onChange={handleChange}
              />
            </div>
          )}

          <div>
            <Label htmlFor="slug">Public URL slug (optional, for SEO)</Label>
            <div className="flex gap-2">
              <Input
                id="slug"
                name="slug"
                placeholder="premium-2-3-4-bhk-homes-kalyan-west"
                value={formData.slug}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, slug: slugify(e.target.value) }))
                }
              />
              <Button
                type="button"
                variant="outline"
                onClick={() =>
                  setFormData((prev) => ({
                    ...prev,
                    slug: slugify(prev.public_display_name || prev.title),
                  }))
                }
              >
                Generate
              </Button>
            </div>
            <p className="mt-1 text-[12px] text-slate-500">
              {formData.slug
                ? `Public page: /p/${formData.slug}`
                : "Blank uses a random short link. A slug makes the URL readable and SEO-friendly."}
            </p>
          </div>

          <div>
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              name="description"
              rows={3}
              placeholder="A short summary shown on the public page."
              value={formData.description}
              onChange={handleChange}
            />
          </div>

          <div>
            <Label htmlFor="contact_id">Contact (optional)</Label>
            <Select
              value={formData.contact_id}
              onValueChange={(val) => {
                handleSelectChange("contact_id", val);
                setContactError("");
              }}
            >
              <SelectTrigger id="contact_id">
                <SelectValue placeholder="Select a contact" />
              </SelectTrigger>
              <SelectContent>
                {contacts.length === 0 ? (
                  <div className="px-2 py-1.5 text-sm text-slate-400">
                    No contacts available
                  </div>
                ) : (
                  contacts.map((contact) => (
                    <SelectItem key={contact.id} value={String(contact.id)}>
                      {contact.name}
                      {contact.phone ? ` · ${contact.phone}` : ""}
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
            {contactError && (
              <p className="mt-1 text-sm text-red-600">{contactError}</p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Status</Label>
              <Select
                value={formData.status}
                onValueChange={(val) => handleSelectChange("status", val)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="under_offer">Under Offer</SelectItem>
                  <SelectItem value="sold">Sold</SelectItem>
                  <SelectItem value="rented">Rented</SelectItem>
                  <SelectItem value="withdrawn">Withdrawn</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Property Type</Label>
              <Select
                value={formData.property_type}
                onValueChange={(val) => handleSelectChange("property_type", val)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select Type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="apartment">Apartment</SelectItem>
                  <SelectItem value="house">House/Villa</SelectItem>
                  <SelectItem value="commercial">Commercial</SelectItem>
                  <SelectItem value="plot">Plot</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="city">City</Label>
              <Input
                id="city"
                name="city"
                placeholder="City"
                value={formData.city}
                onChange={handleChange}
              />
            </div>
            <div>
              <Label htmlFor="locality">Locality</Label>
              <Input
                id="locality"
                name="locality"
                placeholder="e.g. Whitefield"
                value={formData.locality}
                onChange={handleChange}
              />
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <Label htmlFor="bedrooms">Bedrooms</Label>
              <Input
                id="bedrooms"
                name="bedrooms"
                type="number"
                value={formData.bedrooms}
                onChange={handleChange}
              />
            </div>
            <div>
              <Label htmlFor="bathrooms">Bathrooms</Label>
              <Input
                id="bathrooms"
                name="bathrooms"
                type="number"
                value={formData.bathrooms}
                onChange={handleChange}
              />
            </div>
            <div>
              <Label htmlFor="area_sqft">Area (Sqft)</Label>
              <Input
                id="area_sqft"
                name="area_sqft"
                type="number"
                value={formData.area_sqft}
                onChange={handleChange}
              />
            </div>
          </div>

          {/* More specs */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Furnishing</Label>
              <Select
                value={formData.furnishing_status}
                onValueChange={(val) => handleSelectChange("furnishing_status", val)}
              >
                <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="unfurnished">Unfurnished</SelectItem>
                  <SelectItem value="semi_furnished">Semi furnished</SelectItem>
                  <SelectItem value="fully_furnished">Fully furnished</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="facing">Facing</Label>
              <Input id="facing" name="facing" placeholder="e.g. East" value={formData.facing} onChange={handleChange} />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="floor_number">Floor</Label>
              <Input id="floor_number" name="floor_number" type="number" value={formData.floor_number} onChange={handleChange} />
            </div>
            <div>
              <Label htmlFor="total_floors">Total floors</Label>
              <Input id="total_floors" name="total_floors" type="number" value={formData.total_floors} onChange={handleChange} />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="launch_date">Launch date</Label>
              <Input id="launch_date" name="launch_date" type="date" value={formData.launch_date} onChange={handleChange} />
            </div>
            <div>
              <Label htmlFor="possession_date">Possession date</Label>
              <Input id="possession_date" name="possession_date" type="date" value={formData.possession_date} onChange={handleChange} />
            </div>
          </div>

          {/* Amenities */}
          <div>
            <Label>Amenities</Label>
            <div className="mt-1.5">
              <AmenitiesEditor value={amenities} onChange={setAmenities} />
            </div>
          </div>

          {/* Tags */}
          <div>
            <Label>Tags</Label>
            <div className="mt-1.5">
              <TagPicker
                appliedTags={appliedTags}
                onAdd={(tag) => setAppliedTags((ts) => (ts.some((t) => t.id === tag.id) ? ts : [...ts, tag]))}
                onRemove={(tag) => setAppliedTags((ts) => ts.filter((t) => t.id !== tag.id))}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            {isRental ? (
              <>
                <div>
                  <Label htmlFor="rent_amount">Rent Amount (₹)</Label>
                  <Input
                    id="rent_amount"
                    name="rent_amount"
                    type="number"
                    value={formData.rent_amount}
                    onChange={handleChange}
                  />
                </div>
                <div>
                  <Label htmlFor="security_deposit">Security Deposit (₹)</Label>
                  <Input
                    id="security_deposit"
                    name="security_deposit"
                    type="number"
                    value={formData.security_deposit}
                    onChange={handleChange}
                  />
                </div>
              </>
            ) : (
              <div>
                <Label htmlFor="asking_price">Asking Price (₹)</Label>
                <Input
                  id="asking_price"
                  name="asking_price"
                  type="number"
                  value={formData.asking_price}
                  onChange={handleChange}
                />
              </div>
            )}
          </div>
        </div>

        <div className="space-y-2">
          <Label>Photos</Label>
          <ImageUploader
            existing={listing?.photos || []}
            onChange={setPhotos}
            label="property photos"
          />
        </div>

        {/* Video / map embeds */}
        <div className="space-y-2">
          <Label>Video &amp; map links</Label>
          <p className="text-xs text-slate-500">
            YouTube, Vimeo, or Google Maps links embed on the public page. Other hosts are skipped.
          </p>
          <VideoLinksEditor value={videoLinks} onChange={setVideoLinks} />
        </div>

        {/* ── Website & publishing ─────────────────────────────────────── */}
        <Separator />
        <div className="space-y-4">
          <div>
            <h3 className="text-sm font-semibold text-slate-900">Website &amp; publishing</h3>
            <p className="text-xs text-slate-500 mt-0.5">
              Controls how this property appears on the public site. The public page never shows the seller, exact address, or any project name.
            </p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Offer</Label>
              <Select
                value={formData.offer_state}
                onValueChange={(val) => handleSelectChange("offer_state", val)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select offer" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="new">New</SelectItem>
                  <SelectItem value="resale">Resale</SelectItem>
                  <SelectItem value="rental">Rental</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="price_band">Price band (shown publicly)</Label>
              <Input
                id="price_band"
                name="price_band"
                placeholder="e.g. ₹85L – ₹1.1Cr"
                value={formData.price_band}
                onChange={handleChange}
              />
            </div>
          </div>

          <div className="flex items-center justify-between rounded-lg border border-slate-200 p-3">
            <div>
              <Label htmlFor="show_in_portfolio" className="cursor-pointer">
                Feature in “Previous work”
              </Label>
              <p className="text-xs text-slate-500 mt-0.5">
                Show this property on the website’s portfolio / about wall.
              </p>
            </div>
            <Switch
              id="show_in_portfolio"
              checked={formData.show_in_portfolio}
              onCheckedChange={(val) =>
                setFormData((prev) => ({ ...prev, show_in_portfolio: val }))
              }
            />
          </div>
        </div>

        {/* ── Public page content (drag-drop block editor) ─────────────── */}
        <Separator />
        <div className="space-y-3">
          <div>
            <h3 className="text-sm font-semibold text-slate-900">Public page content</h3>
            <p className="text-xs text-slate-500 mt-0.5">
              Compose what buyers see on the public page. Drag to reorder; click a block to edit it.
              Image blocks use the photos above.
            </p>
          </div>

          {listing ? (
            <MicrositeBuilder
              content={websiteContent}
              onChange={setWebsiteContent}
              images={savedPhotos}
              showBackground={false}
            />
          ) : (
            <p className="rounded-lg border border-dashed border-slate-200 bg-slate-50 p-4 text-xs text-slate-500">
              Create the listing first, then reopen it to design the public page and reference the
              photos you’ve uploaded.
            </p>
          )}
        </div>
      </div>

      <div className="flex items-center justify-end gap-2 p-6 bg-slate-50 border-t border-slate-100">
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
          disabled={isPending}
        >
          Cancel
        </Button>
        <Button type="submit" disabled={isPending}>
          {isPending ? "Saving..." : listing ? "Update Listing" : "Create Listing"}
        </Button>
      </div>
    </form>
  );
}
